import HistoryIcon from '@mui/icons-material/History';
import InProgressIcon from '@mui/icons-material/Today';
import {
  NumberInput,
  RecordContextProvider,
  TextField,
  TopToolbar,
  useRemoveFromStore,
  useResourceContext,
} from 'react-admin';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  GetB2bRecurringInvestmentResponse,
  GetB2bRecurringInvestmentsQuery,
  getB2bRecurringInvestmentsSortColumns,
} from '@Common/types/bo/b2b/recurring_investments';

import { planTypeChoices } from '@sh4res/bo-common/choices/plans';
import {
  b2bRecurringInvestmentFrequencyChoices,
  b2bRecurringInvestmentInitiatorChoices,
  b2bRecurringInvestmentPayerChoices,
  b2bRecurringInvestmentStatusChoices,
} from '@sh4res/bo-common/choices/recurring_investments';
import {
  DatagridTyped,
  EmptyScreen,
  FilterButton,
  List,
  VerticalTab,
  VerticalTabsLayout,
} from '@sh4res/bo-common/components';
import { B2bRecurringInvestmentCreate } from '@sh4res/bo-common/components/B2bRecurringInvestments/B2bRecurringInvestmentCreate';
import { SelectColumnsButton } from '@sh4res/bo-common/components/SelectColumnsButton';
import { CurrencyField, IdField, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { EmptyFolderSvg } from '@sh4res/bo-common/graphics';
import { useResourceBasePath, useSelectedColumns } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort } from '@sh4res/bo-common/utils';
import { createFilters, FilterList } from '@sh4res/bo-common/utils/create_filters';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

type RecurringInvestmentTab = 'in_progress' | 'history';

const tabStatusMapping: Record<
  RecurringInvestmentTab,
  GetB2bRecurringInvestmentResponse['status']
> = {
  in_progress: 'active',
  history: 'deleted',
};

const defaultSort: RaSort<GetB2bRecurringInvestmentsQuery> = { field: 'created_at', order: 'DESC' };

const defaultColumns: (keyof GetB2bRecurringInvestmentResponse)[] = [
  'id',
  'initiator',
  'b2b_company_plan_type',
  'execution_frequency',
  'user_input_amount',
  'payer',
  'execution_starting_date',
  'next_execution_at',
  'status',
];

const preference = 'b2b_recurring_investments.list.columns';

const NoRecurringInvestments = () => (
  <EmptyScreen
    label="resources.bo_common.b2b.recurring_investments.empty"
    svg={<EmptyFolderSvg />}
  />
);

const buildFilters = (testId: string) =>
  createFilters<GetB2bRecurringInvestmentsQuery>({
    id: <NumberInput source="" data-testid={`${testId}_IdFilterInput`} />,
    b2b_employee_id: <NumberInput source="" data-testid={`${testId}_EmployeeIdFilterInput`} />,
    b2b_company_plan_id: (
      <NumberInput source="" data-testid={`${testId}_CompanyPlanIdFilterInput`} />
    ),
  } as FilterList<GetB2bRecurringInvestmentsQuery>);

export const B2bRecurringInvestmentsList = ({ testId }: { testId: string }) => {
  const CreateButton = !isBoExternal() ? (
    <B2bRecurringInvestmentCreate
      variant="contained"
      color="info"
      size="small"
      sx={{
        fontWeight: 'bold',
        textTransform: 'capitalize',
      }}
    />
  ) : undefined;

  const resource = useResourceContext();
  const removeFromStore = useRemoveFromStore();
  const navigate = useNavigate();
  const location = useLocation();

  const handleTabClick = (tab: RecurringInvestmentTab) => () => {
    removeFromStore(`${resource}.listParams`);
    const params = new URLSearchParams();
    params.set('tab', tab);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  // Dummy record, because react-admin tabs requires a record to work
  return (
    <RecordContextProvider value={{ id: 1 }}>
      <VerticalTabsLayout>
        <VerticalTab
          icon={<InProgressIcon />}
          label="resources.bo_common.b2b.campaigns.tabs.in_progress.label"
          data-testid={`${testId}_InProgressTab`}
          path="in_progress"
          onClick={handleTabClick('in_progress')}
          button={CreateButton}>
          <ListView tab="in_progress" testId={testId} />
        </VerticalTab>
        <VerticalTab
          icon={<HistoryIcon />}
          label="resources.bo_common.b2b.campaigns.tabs.history.label"
          data-testid={`${testId}_HistoryTab`}
          path="history"
          onClick={handleTabClick('history')}
          button={CreateButton}>
          <ListView tab="history" testId={testId} />
        </VerticalTab>
      </VerticalTabsLayout>
    </RecordContextProvider>
  );
};

const ListView = ({ tab, testId }: { tab: RecurringInvestmentTab; testId: string }) => {
  const resourceBasePath = useResourceBasePath();

  const columns: ColumnList<GetB2bRecurringInvestmentResponse> = {
    id: (
      <IdField
        reference={`${resourceBasePath}recurring_investments`}
        data-testid={`${testId}_IdField`}
      />
    ),
    b2b_employee_id: (
      <IdField
        reference={`${resourceBasePath}employees`}
        data-testid={`${testId}_EmployeeIdField`}
      />
    ),
    b2b_contract_id: (
      <IdField
        reference={`${resourceBasePath}contracts`}
        data-testid={`${testId}_ContractIdField`}
      />
    ),
    b2b_company_plan_id: (
      <IdField<GetB2bRecurringInvestmentResponse>
        source="b2b_company_plan_id"
        reference={(record) =>
          `${resourceBasePath}contracts/${record.b2b_contract_id}/show?tab=plans&product_id=${record.b2b_company_plan_id}`
        }
        data-testid={`${testId}_CompanyPlanIdField`}
      />
    ),
    b2b_company_plan_type: (
      <SelectChipField choices={planTypeChoices} data-testid={`${testId}_CompanyPlanTypeField`} />
    ),
    status: (
      <SelectChipField
        choices={b2bRecurringInvestmentStatusChoices}
        data-testid={`${testId}_StatusField`}
      />
    ),
    execution_frequency: (
      <SelectChipField
        choices={b2bRecurringInvestmentFrequencyChoices}
        data-testid={`${testId}_ExecutionFrequencyField`}
      />
    ),
    user_input_amount: (
      <CurrencyField currency="user_currency" testId={`${testId}_UserInputAmountField`} />
    ),
    user_currency: <TextField data-testid={`${testId}_UserCurrencyField`} />,
    payer: (
      <SelectChipField
        choices={b2bRecurringInvestmentPayerChoices}
        data-testid={`${testId}_PayerField`}
      />
    ),
    initiator: (
      <SelectChipField
        choices={b2bRecurringInvestmentInitiatorChoices}
        data-testid={`${testId}_InitiatorField`}
      />
    ),
    deductibility: <TextField emptyText="-" data-testid={`${testId}_DeductibilityField`} />,
    execution_starting_date: (
      <UTCDateField showTime={false} data-testid={`${testId}_ExecutionStartingDateField`} />
    ),
    next_execution_at: (
      <UTCDateField showTime={false} data-testid={`${testId}_NextExecutionAtField`} />
    ),
    last_executed_at: (
      <UTCDateField showTime emptyText="-" data-testid={`${testId}_LastExecutedAtField`} />
    ),
    created_at: (
      <UTCDateField showTime={!isBoExternal()} data-testid={`${testId}_CreatedAtField`} />
    ),
    updated_at: (
      <UTCDateField showTime={!isBoExternal()} data-testid={`${testId}_UpdatedAtField`} />
    ),
  };

  const displayedColumns = useSelectedColumns<GetB2bRecurringInvestmentResponse>({
    preferences: preference,
    defaultColumns,
  });

  return (
    <List
      actions={
        <TopToolbar>
          <FilterButton data-testid={`${testId}_FilterButton`} />
          <SelectColumnsButton
            preference={preference}
            columns={columns}
            defaultColumns={defaultColumns}
            testId={`${testId}_SelectColumnsButton`}
          />
        </TopToolbar>
      }
      empty={false}
      exporter={false}
      filter={{ status: tabStatusMapping[tab] }}
      filters={buildFilters(testId)}
      sort={defaultSort}
      sx={{ '& .RaList-main': { width: '100%' } }}>
      <DatagridTyped<GetB2bRecurringInvestmentResponse>
        rowClick="show"
        sortableColumns={getB2bRecurringInvestmentsSortColumns}
        columnRegistry={columns}
        displayedColumns={displayedColumns}
        bulkActionButtons={false}
        empty={<NoRecurringInvestments />}
      />
    </List>
  );
};
